import { useState, useEffect, createContext, useContext } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { trackEvent, EVENTS } from '../lib/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      setError(null);
    }, (error) => {
      console.error('Auth state change error:', error);
      setError(error);
      setLoading(false);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'auth_state_change',
        errorMessage: error.message
      });
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      trackEvent(EVENTS.USER_LOGGED_IN);
      return result;
    } catch (error) {
      console.error('Login error:', error);
      setError(error);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'login',
        errorMessage: error.message
      });
      throw error;
    }
  };

  const signup = async (email, password) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      trackEvent(EVENTS.USER_SIGNED_UP);
      return result;
    } catch (error) {
      console.error('Signup error:', error);
      setError(error);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'signup',
        errorMessage: error.message
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      setError(error);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'logout',
        errorMessage: error.message
      });
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    signup,
    logout,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;
